import { createContext, useContext, useEffect, useMemo, useState, type ComponentProps, type ReactNode } from 'react';
import { ActionBarPrimitive, MessagePrimitive, ThreadPrimitive, useMessage, useThread } from '@assistant-ui/react';
import { ArrowDown, Check, Copy, RefreshCw } from 'lucide-react';
import { useEmbeddedIvyChatStore } from '@/stores/embeddedIvyChatStore';
import { OutOfCreditsCard } from '@/components/billing/OutOfCreditsCard';
import { parseOutOfCreditsPart } from '@/services/billingPaywallDetector';
import { parseErrorPart, parseTurnMeta } from '@/lib/ivyChat/chatNotices';
import { toolDisplayName, toolDoneName, toolSummary } from '@/lib/ivyChat/toolNames';
import { IvyTurn, StepsLine, WorkingLine, type ThreadStep } from '@/components/studio/plan/Thread';
import { useIvyMarkState } from '@/lib/ivyChat/ivyMarkState';
import { IvyMarkdownContent } from './IvyMarkdownContent';
import { answeredWords } from '@/components/video-workspace/storyboard/feedbackWords';
import { useOutlineEditQueueStore } from '@/stores/outlineEditQueueStore';
import { ChatErrorNotice } from './ChatNotices';
import { UserTextB } from './StoryboardFeedbackB';
import { StaleMessageGuard } from './StaleMessageGuard';

/**
 * Direction B (beta only): the studio's chat in the front door's language. Your messages are raised bubbles; Ivy's
 * turns sit beside her mark, her tool runs fold into one line (the working line while she works), and each turn's
 * time sits under its "Show steps" instead of a row between turns. No monospace.
 */
export function EmbeddedIvyThreadB({ summary, collapse = false }: { summary?: ReactNode; collapse?: boolean } = {}) {
  const hideBefore = useFoldedHistory(collapse);
  const [open, setOpen] = useState(false);
  const hidden = open ? 0 : hideBefore;
  return (
    <ThreadPrimitive.Root className="flex min-h-0 flex-1 flex-col">
      <ThreadPrimitive.Viewport className="custom-scrollbar flex-1 overflow-y-auto">
        {!summary && (
          <ThreadPrimitive.Empty>
            <p className="px-4 py-8 text-center text-b-meta text-b-t3">Ask Ivy to change a scene, the narration or the plan.</p>
          </ThreadPrimitive.Empty>
        )}
        <div className="mx-auto w-full max-w-[720px] space-y-5 px-4 py-6">
          {summary}
          {hidden > 0 && (
            <button type="button" aria-expanded={false} onClick={() => setOpen(true)} className="block w-full text-center text-b-meta text-b-t3 hover:text-b-t1">
              Earlier in this conversation · {hidden} message{hidden === 1 ? '' : 's'}
            </button>
          )}
          <HiddenBefore.Provider value={hidden}>
            <StaleMessageGuard thread="B">
              <ThreadPrimitive.Messages components={{ UserMessage, AssistantMessage }} />
            </StaleMessageGuard>
          </HiddenBefore.Provider>
          <Thinking />
        </div>
        <ThreadPrimitive.ViewportFooter className="sticky bottom-0 flex justify-center pb-2">
          <ThreadPrimitive.ScrollToBottom asChild>
            <button
              type="button"
              aria-label="Scroll to the latest"
              className="grid h-8 w-8 place-items-center rounded-full bg-b-raised text-b-t2 shadow-[var(--b-shadow-lift)] transition-opacity hover:text-b-t1 disabled:hidden"
            >
              <ArrowDown className="h-4 w-4" />
            </button>
          </ThreadPrimitive.ScrollToBottom>
        </ThreadPrimitive.ViewportFooter>
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  );
}

/** Messages before this index are folded behind "Earlier in this conversation". */
const HiddenBefore = createContext(0);
const useHidden = () => (useMessage() as { index?: number }).index! < useContext(HiddenBefore);

/** Index of Ivy's last turn that draws something: one with no words and no steps (after a reload, a learn turn's) is not. */
function lastDrawnTurn(messages: readonly { role: string; content: unknown }[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant' && foldTurn(messages[i].content as Part[]).blocks.length) return i;
  }
  return -1;
}

/**
 * A reopened video folds its history: everything before Ivy's last turn that draws something. The conversation can
 * arrive in stages, so the cut follows it until Ivy first works during the visit (you sent something), and holds from
 * then on: what is said during the visit comes after it, so it always shows. A background learn turn is not a visit's
 * work.
 */
function useFoldedHistory(collapse: boolean): number {
  const lastIvyTurn = useThread((t) => lastDrawnTurn(t.messages));
  const working = useEmbeddedIvyChatStore((s) => s.isStreaming && s.activeTurnKind !== 'learn');
  const [held, setHeld] = useState<number | null>(null);
  useEffect(() => {
    if (collapse && working && held === null) setHeld(Math.max(lastIvyTurn, 0));
  }, [collapse, working, held, lastIvyTurn]);
  return collapse ? held ?? Math.max(lastIvyTurn, 0) : 0;
}

/** Index of this message, and whether Ivy has answered it yet (F4: a storyboard send reads "Sending…" until she does). */
function UserMessage() {
  const index = (useMessage() as { index?: number }).index ?? 0;
  const answered = useThread((t) => t.messages.slice(index + 1).some((m) => m.role === 'assistant'));
  const hidden = useHidden();
  if (hidden) return null;
  const phase = answered ? 'has-it' : 'sending';
  return (
    <MessagePrimitive.Root className="plan-rise flex flex-col items-end">
      <MessagePrimitive.Content components={{ Text: ({ text }) => <UserTextB text={text} phase={phase} /> }} />
    </MessagePrimitive.Root>
  );
}

/** This session's storyboard sends in the page's words (kept from the send), for "✓ You answered: …". */
const keptChange = (id: string) => {
  const kept = useOutlineEditQueueStore.getState().displayByInput[id];
  return kept?.summaries ? { touched: Object.keys(kept.summaries), fields: {}, lock: false, ...kept } : undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- assistant-ui's content parts, as EmbeddedIvyThread reads them
type Part = any;
type Block = { type: 'text'; text: string } | { type: 'steps'; steps: ThreadStep[] };

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * A step's state from the runtime's own part: its result once done (useMessage() parts carry no status). While the
 * turn runs, a call without its result is still running; once the turn has ended, it is done.
 */
const stepState = (part: Part, turnRunning: boolean): ThreadStep['state'] => {
  if (part.isError || part.status?.type === 'error') return 'error';
  const reported = part.result !== undefined || part.status?.type === 'complete' || part.status?.type === 'incomplete';
  return turnRunning && !reported ? 'running' : 'done';
};

const BUILDERS = new Set(['Agent', 'Task']);

/** A thought's first sentence, as one quiet line. */
const firstSentence = (text: string) => {
  const line = text.trim().split('\n')[0] ?? '';
  return (line.match(/^.*?[.!?](?=\s|$)/)?.[0] ?? line).slice(0, 160);
};

/** What a JSON error body says: its `error`, `error.message` or `message`. */
function jsonError(text: string): string | null {
  if (!text.startsWith('{')) return null;
  try {
    const body = JSON.parse(text);
    const said = typeof body.error === 'string' ? body.error : body.error?.message ?? body.message;
    return typeof said === 'string' ? said : null;
  } catch {
    return null;
  }
}

/** What went wrong, in one line: a JSON body's error, else the first line that is more than a brace. */
const firstLine = (result: unknown) => {
  if (typeof result !== 'string') return null;
  const text = result.trim();
  const line = jsonError(text) ?? text.split('\n').map((l) => l.trim()).find((l) => !/^[{}[\],]*$/.test(l)) ?? '';
  return line.slice(0, 80) || null;
};

/**
 * A turn's parts as Ivy's words and her runs of steps; its time (a TURN_META part) goes with its last run. A builder's
 * own steps (parentToolUseId) go under that builder, wherever they arrive in the turn.
 */
function foldTurn(content: Part[], turnRunning = false): { blocks: Block[]; time: string | null } {
  const blocks: Block[] = [];
  const byId = new Map<string, ThreadStep>();
  let time: string | null = null;
  let steps: ThreadStep[] | null = null;
  const flush = () => {
    if (steps) blocks.push({ type: 'steps', steps });
    steps = null;
  };
  for (const [i, part] of content.entries()) {
    if (part.type === 'text') {
      const meta = parseTurnMeta(part.text ?? '');
      if (meta !== null) time = meta;
      else if ((part.text ?? '').trim()) {
        flush();
        blocks.push({ type: 'text', text: part.text });
      }
    } else if (part.type === 'reasoning') {
      const line = firstSentence(part.text ?? '');
      (steps ??= []).push(...(line ? [{ id: `th-${i}`, kind: 'thought' as const, name: line, done: line, detail: null, state: 'done' as const }] : []));
    } else if (part.type === 'tool-call') {
      const name = part.toolName || 'unknown';
      const args = part.args || {};
      const step: ThreadStep = {
        id: part.toolCallId || `tc-${i}`,
        name: capital(toolDisplayName(name, args)),
        done: capital(toolDoneName(name, args)),
        detail: toolSummary(name, args) || null,
        state: stepState(part, turnRunning),
        startedAt: part.startedAt,
        endedAt: part.endedAt,
        error: part.isError ? firstLine(part.result) : null,
        builder: BUILDERS.has(name),
      };
      const builder = part.parentToolUseId ? byId.get(part.parentToolUseId) : undefined;
      if (builder) (builder.children ??= []).push(step);
      else (steps ??= []).push(step);
      byId.set(step.id, step);
    }
  }
  flush();
  return { blocks, time };
}

function AssistantMessage() {
  const message = useMessage() as Part;
  const hidden = useHidden();
  const running = message.status?.type === 'running';
  const { blocks, time } = useMemo(() => foldTurn(message.content || [], running), [message.content, running]);
  const reply = useThread((t) => {
    const next = t.messages[(message.index ?? 0) + 1] as Part | undefined;
    return next?.role === 'user' ? (next.content as Part[]).find((p) => p.type === 'text')?.text ?? '' : null;
  });
  const checkpoint = { active: !!message.isLast && !running, stale: !message.isLast, answer: reply ? answeredWords(reply, keptChange) : undefined };
  const lastSteps = blocks.map((b) => b.type).lastIndexOf('steps');

  if (hidden || !blocks.length) return null;
  const Turn = message.isLast ? LiveIvyTurn : IvyTurn;
  return (
    <MessagePrimitive.Root className="plan-rise group">
      <Turn aside={!running && <Actions />}>
        {blocks.map((block, i) =>
          block.type === 'text' ? (
            <AssistantText key={`t-${i}`} text={block.text} checkpoint={checkpoint} />
          ) : (
            <StepsLine key={`s-${i}`} steps={block.steps} running={running && i === blocks.length - 1} time={i === lastSteps ? time : null} />
          ),
        )}
      </Turn>
    </MessagePrimitive.Root>
  );
}

function AssistantText({ text, checkpoint }: { text: string; checkpoint: { active: boolean; stale: boolean } }) {
  if (text.startsWith('[COMPACTION_SUMMARY]\n')) return <Compacted summary={text.slice('[COMPACTION_SUMMARY]\n'.length)} />;
  const paywall = parseOutOfCreditsPart(text);
  if (paywall) return <OutOfCreditsCard paywall={paywall} />;
  const error = parseErrorPart(text);
  if (error !== null) return <ChatErrorNotice message={error} />;
  return (
    <div className="prose-chat text-b-read text-b-t1">
      <IvyMarkdownContent content={text} checkpoint={checkpoint} />
    </div>
  );
}

function Compacted({ summary }: { summary: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="text-b-meta text-b-t3">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="hover:text-b-t1">
        Earlier messages summarised · {open ? 'Hide' : 'Show'}
      </button>
      {open && <p className="mt-2 whitespace-pre-wrap">{summary}</p>}
    </div>
  );
}

/** Whether the thinking line shows: before Ivy's first words of a turn, and while she learns after it. */
function useThinking(): { shown: boolean; learning: boolean } {
  const isStreaming = useEmbeddedIvyChatStore((s) => s.isStreaming);
  const learning = useEmbeddedIvyChatStore((s) => s.activeTurnKind === 'learn');
  const last = useEmbeddedIvyChatStore((s) => s.messages[s.messages.length - 1]);
  const shown = isStreaming && (learning || !last || ['user', 'init', 'system'].includes(last.type));
  return { shown, learning };
}

/** Ivy's newest turn: her mark shows what she is doing now, unless the thinking line below carries it. */
function LiveIvyTurn(props: Omit<ComponentProps<typeof IvyTurn>, 'mark'>) {
  const mark = useIvyMarkState();
  const { shown } = useThinking();
  return <IvyTurn {...props} mark={shown ? undefined : mark} />;
}

/** Before Ivy's first words of a turn: her live mark and the working line, as on /start. */
function Thinking() {
  const mark = useIvyMarkState();
  const { shown, learning } = useThinking();
  if (!shown) return null;
  return (
    <IvyTurn mark={mark}>
      <WorkingLine text={learning ? 'Learning from this session…' : 'Thinking…'} />
    </IvyTurn>
  );
}

function Actions() {
  const [copied, setCopied] = useState(false);
  return (
    <ActionBarPrimitive.Root className="flex flex-col items-center gap-0.5 text-b-t3 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
      <ActionBarPrimitive.Copy asChild onClick={() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
        <button type="button" aria-label="Copy" className="rounded-[var(--b-radius-control)] p-1 hover:bg-b-raised hover:text-b-t1">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Reload asChild>
        <button type="button" aria-label="Try again" className="rounded-[var(--b-radius-control)] p-1 hover:bg-b-raised hover:text-b-t1">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </ActionBarPrimitive.Reload>
    </ActionBarPrimitive.Root>
  );
}
